import * as Sentry from "@sentry/react";

// Injected at build time by vite `define` (see vite.config.ts). Always defined
// (possibly empty string), so guarding on falsiness is safe.
declare const __SENTRY_DSN__: string;
declare const __SENTRY_ENVIRONMENT__: string;
declare const __SENTRY_RELEASE__: string;

let enabled = false;

export function isSentryEnabled(): boolean {
  return enabled;
}

export function initSentry(): void {
  const dsn = typeof __SENTRY_DSN__ !== "undefined" ? __SENTRY_DSN__ : "";
  if (!dsn) return; // graceful no-op when unconfigured

  const environment =
    (typeof __SENTRY_ENVIRONMENT__ !== "undefined" && __SENTRY_ENVIRONMENT__) ||
    "development";

  try {
    Sentry.init({
      dsn,
      environment,
      release:
        (typeof __SENTRY_RELEASE__ !== "undefined" && __SENTRY_RELEASE__) ||
        undefined,
      integrations: [Sentry.browserTracingIntegration()],
      // Performance monitoring: sample fewer transactions in production.
      tracesSampleRate: environment === "production" ? 0.1 : 1.0,
      // This app handles PHI — do not attach IPs or other default PII.
      sendDefaultPii: false,
    });
    enabled = true;
  } catch {
    enabled = false;
  }
}

export function setSentryUser(id: string | null): void {
  if (!enabled) return;
  try {
    Sentry.setUser(id ? { id } : null);
  } catch {
    /* noop */
  }
}

export function setSentryTag(key: string, value: string): void {
  if (!enabled) return;
  try {
    Sentry.setTag(key, value);
  } catch {
    /* noop */
  }
}

export function captureException(
  err: unknown,
  context?: {
    tags?: Record<string, string | number | boolean>;
    extra?: Record<string, unknown>;
  },
): void {
  if (!enabled) return;
  try {
    Sentry.withScope((scope) => {
      if (context?.tags) scope.setTags(context.tags);
      if (context?.extra) scope.setExtras(context.extra);
      Sentry.captureException(err);
    });
  } catch {
    /* never let error reporting break the UI */
  }
}

export { Sentry };
